import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatDate, formatDateTime } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Receipt, FileText, CalendarRange, Building2, Clock, Layers, Eye, RefreshCw, XCircle, Download, Pencil, RotateCcw, History, AlertTriangle, Send, CheckCircle2, Mail, MapPin, ExternalLink, FileMinus, Ban, Archive, ChevronUp, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";
import InvoicePreview from "@/components/InvoicePreview";
import SendInvoiceDialog from "@/components/SendInvoiceDialog";
import JobInvoicePreview from "@/components/JobInvoicePreview";
import DayProfitBreakdown from "@/components/DayProfitBreakdown";
import EditDayDialog from "@/components/EditDayDialog";
import AdminEditJob from "@/components/AdminEditJob";

const PAYMENT_TERMS = ["COD", "7 Days", "14 Days", "30 Days"];
const XERO_SYNCED_MSG = "This invoice has already been synchronised with Xero. Please create a Credit Note, Void the invoice (if appropriate), or update it through the Xero integration.";
const isSynced = (inv) => !!inv.xero_invoice_id || ["synced_to_xero", "sent", "paid"].includes(inv.status);

const INV_STATUS = {
  draft: { label: "Draft", cls: "bg-slate-200 text-slate-700" },
  ready_to_sync: { label: "Ready to Sync", cls: "bg-amber-100 text-amber-800" },
  synced_to_xero: { label: "Synced to Xero", cls: "bg-blue-100 text-blue-700" },
  sent: { label: "Sent", cls: "bg-indigo-100 text-indigo-700" },
  paid: { label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700" },
};
const NEXT_STATUSES = ["draft", "ready_to_sync", "synced_to_xero", "sent", "paid", "cancelled"];
const XERO_STATUS_CLS = {
  "draft": "bg-slate-200 text-slate-700",
  "awaiting approval": "bg-amber-100 text-amber-800",
  "awaiting payment": "bg-blue-100 text-blue-700",
  "paid": "bg-emerald-100 text-emerald-700",
  "void": "bg-red-100 text-red-700",
  "deleted": "bg-red-100 text-red-700",
};
const money = (n) => `$${(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function WeeklyBilling() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [week, setWeek] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState({});
  const [ready, setReady] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [creating, setCreating] = useState(false);
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reopenInv, setReopenInv] = useState(null);
  const [auditInv, setAuditInv] = useState(null);
  const [sendInv, setSendInv] = useState(null);
  const [sendResend, setSendResend] = useState(false);
  const [wbExpanded, setWbExpanded] = useState({});
  const toggleExpanded = (id) => setWbExpanded((s) => ({ ...s, [id]: !s[id] }));
  const [jobEdit, setJobEdit] = useState(null);
  const [editDay, setEditDay] = useState(null);
  const [jobPreviewRefresh, setJobPreviewRefresh] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const unpaidOnly = searchParams.get("filter") === "unpaid";
  const navigate = useNavigate();
  const [showArchived, setShowArchived] = useState(false);
  const baseInvoices = (unpaidOnly ? invoices.filter((i) => !["paid", "cancelled"].includes(i.status)) : invoices).filter((i) => !["awaiting_payment", "paid"].includes(i.status));
  const shownInvoices = baseInvoices.filter((i) => i.status !== "cancelled");
  const cancelledInvoices = invoices.filter((i) => i.status === "cancelled");

  const loadInvoices = () => api.get("/invoices").then((r) => setInvoices(r.data));
  const loadReady = () => api.get("/billing-ready").then((r) => setReady(r.data));
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)); loadInvoices(); loadReady(); }, []);

  const [lastJobIds, setLastJobIds] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const loadDockets = async (pid = projectId, wk = week, jids = lastJobIds) => {
    const list = (Array.isArray(jids) ? jids : (jids ? [jids] : [])).filter(Boolean);
    if (!pid && list.length === 0) { toast.error("Select a project/site first"); return; }
    const qs = `project_id=${pid || ""}&week_start=${wk}${list.length ? `&job_ids=${list.join(",")}` : ""}`;
    const r = await api.get(`/weekly-billing?${qs}`);
    setLastJobIds(list);
    setData(r.data);
    const sel = {}; const exp = {}; r.data.dockets.forEach((d) => { sel[d.id] = true; exp[d.id] = true; });
    setSelected(sel);
    setWbExpanded(exp);
  };

  const openJobEdit = async (id) => {
    try {
      const r = await api.get(`/jobs/${id}`);
      setJobEdit(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Tap a Ready-for-Billing card to add/remove it — several jobs load together, date-ordered.
  const pickGroup = (g) => {
    const next = selectedGroups.includes(g.job_id)
      ? selectedGroups.filter((x) => x !== g.job_id)
      : [...selectedGroups, g.job_id];
    setSelectedGroups(next);
    setProjectId(g.project_id);
    setWeek(g.week_start);
    if (next.length === 0) { setData(null); setLastJobIds([]); return; }
    loadDockets(g.project_id, g.week_start, next);
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const chosenIds = data ? data.dockets.filter((d) => selected[d.id]).map((d) => d.id) : [];
  const [rtbBusy, setRtbBusy] = useState(false);
  const downloadReadyPdf = async () => {
    setRtbBusy(true);
    try {
      const params = { project_id: projectId || "", week_start: week, selected: chosenIds.join(","), invoice_notes: invoiceNotes, invoice_date: invoiceDate };
      if (lastJobIds.length) params.job_ids = lastJobIds.join(",");
      const res = await api.get("/weekly-billing/summary-pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `ready-to-bill-internal-${data?.week_start || week}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Internal Ready to Bill PDF downloaded");
    } catch (e) { toast.error("Could not generate the Ready to Bill PDF"); }
    finally { setRtbBusy(false); }
  };

  const normAddr = (s) => (s || "").trim().toLowerCase();
  const addrCounts = {};
  ready.forEach((g) => { const k = normAddr(g.site_address); if (k) addrCounts[k] = (addrCounts[k] || 0) + 1; });
  // Tick every ready job that shares this exact site address (across weeks/months) and load them together.
  const addAllAtAddress = (g) => {
    const ids = ready.filter((x) => normAddr(x.site_address) === normAddr(g.site_address)).map((x) => x.job_id);
    setSelectedGroups(ids);
    setProjectId(g.project_id);
    setWeek(g.week_start);
    loadDockets(g.project_id, g.week_start, ids);
    toast.success(`Selected ${ids.length} job${ids.length !== 1 ? "s" : ""} at this address — combine into one invoice`);
  };

  const openPreview = async () => {
    if (chosenIds.length === 0) { toast.error("Select at least one docket"); return; }
    try {
      const r = await api.post("/invoices/preview", { project_id: projectId, week_start: week, docket_ids: chosenIds, invoice_notes: invoiceNotes, invoice_date: invoiceDate || undefined });
      setPreview(r.data); setPreviewOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [docketBusy, setDocketBusy] = useState(false);
  const combinedDocketPreview = async () => {
    if (chosenIds.length === 0) { toast.error("Select at least one docket"); return; }
    setDocketBusy(true);
    try {
      const res = await api.post("/invoices/combined-docket-preview", { project_id: projectId, week_start: week, docket_ids: chosenIds }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "combined-docket-preview.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate combined docket PDF"); }
    finally { setDocketBusy(false); }
  };

  const createInvoice = async () => {
    if (chosenIds.length === 0) { toast.error("Select at least one docket"); return; }
    setCreating(true);
    try {
      const r = await api.post("/invoices", { project_id: projectId, week_start: week, docket_ids: chosenIds, invoice_notes: invoiceNotes, invoice_date: invoiceDate || undefined });
      toast.success(`Draft invoice ${r.data.invoice_number} ready — review & send`);
      setData(null); setPreviewOpen(false); setInvoiceNotes(""); setInvoiceDate(""); setSelectedGroups([]);
      await loadInvoices(); loadReady();
      // Streamlined flow: open the existing Send / Sync confirmation popup for the new draft.
      // Nothing is created in Xero until the user completes that popup.
      setSendResend(false); setSendInv(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const cancelInvoice = async (inv) => {
    if (!window.confirm(`Cancel ${inv.invoice_number}? Its dockets return to Ready to Bill and the number is retired.`)) return;
    try { await api.post(`/invoices/${inv.id}/cancel`); toast.success(`${inv.invoice_number} cancelled`); loadInvoices(); loadReady(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const refreshXero = async (inv) => {
    try { await api.post(`/invoices/${inv.id}/refresh-xero`); toast.success("Refreshed status & payment from Xero"); loadInvoices(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [xeroBusy, setXeroBusy] = useState("");
  const openInXero = (inv) => window.open(`https://go.xero.com/app/invoicing/view/${inv.xero_invoice_id}`, "_blank", "noopener");
  const resyncXero = async (inv) => {
    setXeroBusy(`resync-${inv.id}`);
    try { const r = await api.post(`/invoices/${inv.id}/xero-resync`); toast.success("Re-synced to Xero"); loadInvoices(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setXeroBusy(""); }
  };
  const voidXero = async (inv) => {
    if (!window.confirm(`Void ${inv.invoice_number} in Xero? This cannot be undone. (Only allowed if unpaid.)`)) return;
    setXeroBusy(`void-${inv.id}`);
    try { await api.post(`/invoices/${inv.id}/xero-void`); toast.success(`${inv.invoice_number} voided in Xero`); loadInvoices(); loadReady(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setXeroBusy(""); }
  };
  const creditNoteXero = async (inv) => {
    if (!window.confirm(`Create a Xero Credit Note for ${inv.invoice_number}? It will be allocated to the invoice where possible.`)) return;
    setXeroBusy(`cn-${inv.id}`);
    try { const r = await api.post(`/invoices/${inv.id}/xero-credit-note`); toast.success(`Credit Note ${r.data.credit_note_number || ""} created`); loadInvoices(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setXeroBusy(""); }
  };

  const downloadInvoicePdf = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number || "invoice"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate invoice PDF"); }
  };

  const openEditInvoice = (inv) => {
    setEditInv(inv);
    setEditForm({
      invoice_date: (inv.invoice_date || "").slice(0, 10),
      payment_terms: inv.payment_terms || "30 Days",
      billing_company: inv.billing_company || "",
      billing_contact: inv.billing_contact || "",
      billing_address: inv.billing_address || "",
      billing_email: inv.billing_email || "",
      accounts_email: inv.accounts_email || "",
      po_number: inv.po_number || "",
      abn: inv.abn || "",
      invoice_notes: inv.invoice_notes || "",
    });
  };

  const saveInvoiceEdit = async () => {
    try {
      await api.patch(`/invoices/${editInv.id}`, editForm);
      toast.success("Invoice updated"); setEditInv(null); loadInvoices();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setEf = (k, v) => setEditForm((f) => ({ ...f, [k]: v }));

  const reopenInvoice = async (dest) => {
    try {
      const r = await api.post(`/invoices/${reopenInv.id}/reopen`, { destination: dest });
      toast.success(`${reopenInv.invoice_number} reopened — ${r.data.jobs} job(s) returned to ${dest === "waiting_to_invoice" ? "Waiting to Invoice" : "Ready to Bill"}`);
      setReopenInv(null); loadInvoices(); loadReady();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-navy flex items-center gap-2"><Receipt className="w-8 h-8" />Ready to Bill</h1>
        <p className="text-slate-500 mt-1">Group daily service dockets into a weekly invoice per site.</p>
      </div>

      {/* Selection */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <Label>Site / Project</Label>
            <Select value={projectId || undefined} onValueChange={setProjectId}>
              <SelectTrigger data-testid="wb-project-select" className="mt-1"><SelectValue placeholder="Select site / project…" /></SelectTrigger>
              <SelectContent>
                {projects.map((p) => {
                  const billing = p.customer_name || p.builder || "Unnamed";
                  const addr = p.site_address || (p.name !== p.customer_name ? p.name : "");
                  return <SelectItem key={p.id} value={p.id}>{billing}{addr ? ` — ${addr}` : ""}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Billing Week (any day)</Label><Input data-testid="wb-week-input" type="date" value={week} onChange={(e) => setWeek(e.target.value)} className="mt-1" /></div>
          <div className="flex items-end"><Button data-testid="wb-load-button" onClick={() => { setSelectedGroups([]); loadDockets(projectId, week, []); }} className="bg-navy hover:bg-navy-light w-full">Load Dockets</Button></div>
        </div>
        {projects.length === 0 && <p className="text-xs text-slate-400 mt-3">No projects yet. Add a "Project / Site" name when creating a job to group its daily visits.</p>}
      </div>

      {/* Ready for Billing — auto-collected groups so nothing is missed */}
      {ready.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h2 className="font-heading font-semibold text-navy mb-1 flex items-center gap-2"><Layers className="w-5 h-5" />Ready for Billing</h2>
          <p className="text-sm text-slate-500 mb-4">Completed & signed jobs awaiting invoicing. Tap one or more jobs to load them together — all days show date-ordered and bill onto one combined invoice & docket PDF.</p>
          {(() => {
            const byWeek = {};
            ready.forEach((g) => { (byWeek[g.week_start] = byWeek[g.week_start] || []).push(g); });
            const weeks = Object.keys(byWeek).sort().reverse();
            return weeks.map((wk) => {
              const groups = byWeek[wk];
              const weekTotal = groups.reduce((s, g) => s + (g.total_inc_gst || 0), 0);
              const weekDockets = groups.reduce((s, g) => s + (g.count || 0), 0);
              return (
                <div key={wk} className="mb-5 last:mb-0" data-testid={`ready-week-${wk}`}>
                  <div className="flex items-center justify-between bg-navy/5 rounded-lg px-4 py-2 mb-3">
                    <p className="text-sm font-semibold text-navy">Period from {formatDate(wk)} → {formatDate(groups[0].week_end)}</p>
                    <p className="text-sm text-navy">
                      <span className="text-slate-500 mr-2">{weekDockets} docket{weekDockets !== 1 ? "s" : ""} · {groups.length} job{groups.length !== 1 ? "s" : ""}</span>
                      <span className="font-bold" data-testid={`ready-week-total-${wk}`}>{money(weekTotal)} inc GST</span>
                    </p>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {groups.map((g) => {
                      const isSel = selectedGroups.includes(g.job_id);
                      return (
                      <button key={g.job_id} data-testid={`ready-group-${g.job_id}`} onClick={() => pickGroup(g)}
                        className={`text-left border rounded-xl p-4 transition-colors relative ${isSel ? "border-navy bg-navy/5 ring-2 ring-navy/30" : "border-slate-200 hover:border-navy/30 hover:bg-slate-50"}`}>
                        {isSel && <span data-testid={`ready-selected-${g.job_id}`} className="absolute top-2 right-2 w-5 h-5 rounded-full bg-navy text-white flex items-center justify-center"><Check className="w-3.5 h-3.5" /></span>}
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-semibold text-navy truncate">{g.customer_name || g.builder || "Unnamed"}</p>
                          <span className="text-sm font-bold text-navy shrink-0">{money(g.total_inc_gst)}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate">{g.site_address}</p>
                        {addrCounts[normAddr(g.site_address)] > 1 && (
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span data-testid={`same-address-flag-${g.job_id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">
                              {addrCounts[normAddr(g.site_address)]} jobs at this address
                            </span>
                            <span role="button" tabIndex={0} data-testid={`combine-address-${g.job_id}`}
                              onClick={(e) => { e.stopPropagation(); addAllAtAddress(g); }}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); addAllAtAddress(g); } }}
                              className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-navy text-white hover:bg-navy-light cursor-pointer">
                              Combine all
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs font-mono text-slate-400">{g.job_number}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${g.billing_cycle === "monthly" ? "bg-violet-100 text-violet-700" : "bg-sky-100 text-sky-700"}`}>{g.billing_cycle === "monthly" ? "Monthly" : "Weekly"}</span>
                          <span className="text-[10px] text-slate-400">{g.period_label}</span>
                        </div>
                        <p className="text-xs text-amber-700 font-semibold mt-1">{g.count} docket{g.count !== 1 ? "s" : ""}</p>
                      </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* Eligible dockets */}
      {data && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><Layers className="w-5 h-5" />{data.project?.name || "Site"}</h2>
              <p className="text-sm text-slate-500 flex items-center gap-3 mt-1">
                {data.project?.builder && <span className="flex items-center gap-1"><Building2 className="w-4 h-4" />{data.project.builder}</span>}
                <span className="flex items-center gap-1"><CalendarRange className="w-4 h-4" />{formatDate(data.week_start)} → {formatDate(data.week_end)}</span>
              </p>
            </div>
            <div className="text-right text-sm">
              <p className="text-navy font-semibold">{data.count} completed dockets</p>
              <p className="text-slate-500 flex items-center gap-1 justify-end"><Clock className="w-4 h-4" />{data.total_hours} hrs total</p>
              <p className="text-emerald-700 font-bold text-base mt-1" data-testid="wb-total-amount">{money(data.total_amount)} <span className="text-xs font-normal text-slate-400">ex GST</span></p>
              <p className="text-xs text-slate-500">+ GST {money(data.gst)} = <span className="font-semibold text-navy">{money(data.total_inc_gst)}</span> inc GST</p>
            </div>
          </div>

          {data.dockets.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No completed & signed dockets ready for this site & week.</p>
          ) : (
            <>
            <div className="flex justify-end -mb-1">
              {(() => {
                const allOpen = data.dockets.every((d) => wbExpanded[d.id]);
                return (
                  <button type="button" data-testid="wb-toggle-all" onClick={() => {
                    const next = {}; data.dockets.forEach((d) => { next[d.id] = !allOpen; }); setWbExpanded(next);
                  }} className="text-xs font-medium text-navy flex items-center gap-1 hover:underline">
                    <Eye className="w-3.5 h-3.5" />{allOpen ? "Collapse all" : "Expand all"}
                  </button>
                );
              })()}
            </div>
            <div className="divide-y divide-slate-100">
              {data.dockets.map((d) => (
                <div key={d.id} data-testid={`wb-docket-${d.id}`} className="py-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox data-testid={`wb-check-${d.id}`} checked={!!selected[d.id]} onCheckedChange={() => toggle(d.id)} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy">{formatDate(d.appointment_date)} · {d.job_number}{d.docket_count > 1 ? ` · ${d.docket_count} days` : ""}</p>
                      <p className="text-xs text-slate-500 truncate">{d.description_of_works || d.scope_of_works || "Caulking works"}</p>
                    </div>
                    <span className="text-xs text-slate-400">{d.hours} hrs</span>
                    <span className="text-sm font-semibold text-navy w-20 text-right" data-testid={`wb-amount-${d.id}`}>{money(d.amount)}</span>
                    <button type="button" data-testid={`wb-preview-${d.id}`} onClick={(e) => { e.preventDefault(); toggleExpanded(d.id); }} className="text-navy text-xs flex items-center gap-1 font-medium"><Eye className="w-3.5 h-3.5" />{wbExpanded[d.id] ? "Hide" : "Review"}</button>
                    <button type="button" data-testid={`wb-edit-${d.id}`} onClick={(e) => { e.preventDefault(); openJobEdit(d.id); }} className="text-amber-700 text-xs flex items-center gap-1"><Pencil className="w-3.5 h-3.5" />Edit</button>
                    <button type="button" onClick={(e) => { e.preventDefault(); navigate(`/jobs/${d.id}`); }} className="text-blue-700 text-xs flex items-center gap-1"><FileText className="w-3.5 h-3.5" />Docket</button>
                  </label>
                  {(d.charge_lines || []).length > 0 && !wbExpanded[d.id] && (
                    <div className="ml-8 mt-1.5 space-y-0.5">
                      {d.charge_lines.map((l, i) => (
                        <div key={i} className="flex items-center justify-between text-xs text-slate-500">
                          <span>{l.description}{l.qty ? ` × ${l.qty}` : ""}</span>
                          <span>{money(l.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {wbExpanded[d.id] && (
                    <div className="mt-2 -mx-0 rounded-xl border border-slate-100 overflow-hidden">
                      {d.multi_day && (d.days || []).length > 0 && (
                        <div className="p-4 bg-slate-50/40 border-b border-slate-100">
                          <DayProfitBreakdown days={d.days || []} daysTotal={d.days_total} jobId={d.id}
                            onEditDay={(day) => setEditDay({ jobId: d.id, index: day.index })} />
                        </div>
                      )}
                      <JobInvoicePreview
                        jobId={d.id}
                        open={wbExpanded[d.id]}
                        onOpenFull={() => navigate(`/jobs/${d.id}`)}
                        onEditInvoice={(fullJob) => setJobEdit(fullJob)}
                        refreshKey={jobPreviewRefresh}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            </>
          )}

          {data.dockets.length > 0 && (
            <div className="space-y-3">
              <div>
                <Label>Internal Invoice Notes <span className="text-slate-400 font-normal">(office use only)</span></Label>
                <textarea data-testid="wb-invoice-notes" rows={2} value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm" placeholder="Notes for this invoice (not shown to customer)…" />
              </div>
              <div>
                <Label>Invoice Date <span className="text-slate-400 font-normal">(leave blank for automatic — set this when combining jobs across months)</span></Label>
                <Input data-testid="wb-invoice-date" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="mt-1" />
                <p className="text-[11px] text-slate-400 mt-1">Each line keeps its own work date and stays in its work-month for revenue; this only sets the single date on the combined invoice.</p>
              </div>
              <Button data-testid="wb-preview-invoice" onClick={openPreview} disabled={chosenIds.length === 0} className="w-full bg-navy hover:bg-navy-light py-6 text-base">
                <Eye className="w-5 h-5 mr-2" />Preview Invoice ({chosenIds.length} dockets)
              </Button>
              <Button data-testid="wb-combined-docket" variant="outline" onClick={combinedDocketPreview} disabled={chosenIds.length === 0 || docketBusy} className="w-full mt-2 bg-white">
                <FileText className="w-4 h-4 mr-2" />{docketBusy ? "Preparing…" : "Preview Combined Docket PDF"}
              </Button>
              <Button data-testid="wb-download-ready-pdf" variant="outline" onClick={downloadReadyPdf} disabled={rtbBusy} className="w-full mt-2 bg-white border-red-200 text-red-700 hover:bg-red-50">
                <Download className="w-4 h-4 mr-2" />{rtbBusy ? "Preparing…" : "Download Ready to Bill PDF"}
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-red-500">Internal only</span>
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Existing weekly invoices */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="font-heading font-semibold text-navy">Weekly Invoices</h2>
          {unpaidOnly && (
            <span data-testid="invoice-unpaid-filter" className="inline-flex items-center gap-2 bg-rose-50 text-rose-700 border border-rose-100 rounded-full px-3 py-1 text-sm font-medium">
              Outstanding (unpaid) only
              <button data-testid="clear-invoice-filter" onClick={() => setSearchParams({})} className="hover:text-red-600">✕</button>
            </span>
          )}
        </div>
        {shownInvoices.length === 0 ? <p className="text-slate-400 text-sm">{unpaidOnly ? "No outstanding invoices." : "No weekly invoices yet."}</p> : (
          <div className="space-y-3">
            {shownInvoices.map((inv) => {
              const st = INV_STATUS[inv.status] || INV_STATUS.draft;
              return (
                <div key={inv.id} data-testid={`invoice-${inv.id}`} className="border border-slate-100 rounded-xl p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-navy flex items-center gap-2">
                        <span data-testid={`invoice-number-${inv.id}`} className="font-mono text-sm bg-navy/5 text-navy px-2 py-0.5 rounded">{inv.invoice_number || "—"}</span>
                        <span data-testid={`invoice-customer-${inv.id}`}>{inv.customer_name || inv.billing_company || "—"}</span>
                      </p>
                      <p data-testid={`invoice-site-${inv.id}`} className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0" />{inv.site_address || inv.project_name || "No site address"}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">{formatDate(inv.week_start)} → {formatDate(inv.week_end)} · {inv.line_items?.length || 0} lines · <span className="font-semibold text-navy">{money(inv.total_inc_gst ?? inv.total_amount)} inc GST</span></p>
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {inv.billing_company && <span>Bill to: {inv.billing_company} · </span>}
                        Invoice: {formatDate(inv.invoice_date)} · Due: {formatDate(inv.due_date)} · {inv.payment_terms || "30 Days"}
                        {inv.po_number && <span> · PO {inv.po_number}</span>}
                      </p>
                      {inv.xero_invoice_id && <p className="text-[11px] text-blue-600 mt-0.5">Xero: {inv.xero_invoice_id}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${st.cls}`}>{st.label}</span>
                    </div>
                  </div>
                  {isSynced(inv) ? (
                    <div className="mt-3 space-y-2">
                      {inv.emailed_at ? (
                        <div data-testid={`invoice-sent-banner-${inv.id}`} className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>Invoice {inv.invoice_number} successfully synced to Xero and emailed to {inv.email_to} on {formatDateTime(inv.emailed_at)}.</span>
                        </div>
                      ) : (
                        <div data-testid={`invoice-synced-noemail-${inv.id}`} className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 text-xs">
                          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>Synced to Xero ({inv.xero_invoice_id}) but not yet emailed. Use Resend Invoice to email it.</span>
                        </div>
                      )}
                      <div data-testid={`invoice-synced-warning-${inv.id}`} className="flex items-start gap-2 bg-slate-50 border border-slate-200 text-slate-600 rounded-lg px-3 py-2 text-[11px]">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /><span>This invoice is synced to Xero and locked for editing. To change it, use the Xero actions below (Re-sync while Draft, or Credit Note / Void once approved).</span>
                      </div>
                      {/* Xero status + payment panel */}
                      <div data-testid={`invoice-xero-panel-${inv.id}`} className="rounded-lg border border-blue-100 bg-blue-50/40 px-3 py-2.5">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-slate-500">Xero status:</span>
                            <span data-testid={`invoice-xero-status-${inv.id}`} className={`px-2 py-0.5 rounded-full font-semibold ${XERO_STATUS_CLS[(inv.xero_status_display || "").toLowerCase()] || "bg-slate-100 text-slate-600"}`}>{inv.xero_status_display || "Not fetched"}</span>
                            {inv.xero_pdf_attached === false && <span className="text-amber-600 text-[11px]">· PDF not attached</span>}
                            {inv.xero_credit_note_number && <span className="text-[11px] text-purple-700">· Credit Note {inv.xero_credit_note_number}</span>}
                          </div>
                          {inv.xero_refreshed_at && <span className="text-[10px] text-slate-400">Updated {formatDateTime(inv.xero_refreshed_at)}</span>}
                        </div>
                        {typeof inv.amount_paid === "number" && (
                          <div className="grid grid-cols-3 gap-2 mt-2 text-xs" data-testid={`invoice-payment-${inv.id}`}>
                            <div><p className="text-slate-400 text-[10px] uppercase tracking-wider">Amount Paid</p><p className="font-semibold text-emerald-700">{money(inv.amount_paid)}</p></div>
                            <div><p className="text-slate-400 text-[10px] uppercase tracking-wider">Outstanding</p><p className="font-semibold text-navy">{money(inv.amount_due ?? 0)}</p></div>
                            <div><p className="text-slate-400 text-[10px] uppercase tracking-wider">Paid Date</p><p className="font-semibold text-navy">{inv.fully_paid_on ? formatDate(inv.fully_paid_on) : "—"}</p></div>
                          </div>
                        )}
                      </div>
                      {/* Xero actions */}
                      <div className="flex flex-wrap gap-2">
                        {!String(inv.xero_invoice_id || "").startsWith("XERO-SIM") && (
                          <Button size="sm" variant="outline" data-testid={`invoice-open-xero-${inv.id}`} onClick={() => openInXero(inv)} className="text-[#13B5EA] border-[#13B5EA]/40">
                            <ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open in Xero
                          </Button>
                        )}
                        <Button size="sm" variant="outline" data-testid={`invoice-refresh-xero-${inv.id}`} onClick={() => refreshXero(inv)}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh Status
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`invoice-resync-${inv.id}`} onClick={() => resyncXero(inv)} disabled={xeroBusy === `resync-${inv.id}`}>
                          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />{xeroBusy === `resync-${inv.id}` ? "Re-syncing…" : "Re-sync"}
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`invoice-credit-note-${inv.id}`} onClick={() => creditNoteXero(inv)} disabled={xeroBusy === `cn-${inv.id}`} className="text-purple-700 border-purple-200">
                          <FileMinus className="w-3.5 h-3.5 mr-1.5" />{xeroBusy === `cn-${inv.id}` ? "Creating…" : "Create Credit Note"}
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`invoice-void-${inv.id}`} onClick={() => voidXero(inv)} disabled={xeroBusy === `void-${inv.id}`} className="text-red-600 border-red-200">
                          <Ban className="w-3.5 h-3.5 mr-1.5" />{xeroBusy === `void-${inv.id}` ? "Voiding…" : "Void Invoice"}
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" data-testid={`invoice-pdf-${inv.id}`} onClick={() => downloadInvoicePdf(inv)}>
                          <Download className="w-3.5 h-3.5 mr-1.5" />Download Invoice PDF
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`invoice-resend-${inv.id}`} onClick={() => { setSendResend(true); setSendInv(inv); }}>
                          <Mail className="w-3.5 h-3.5 mr-1.5" />Resend Invoice
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`invoice-audit-${inv.id}`} onClick={() => setAuditInv(auditInv === inv.id ? null : inv.id)}>
                          <History className="w-3.5 h-3.5 mr-1.5" />Audit History
                        </Button>
                      </div>
                    </div>
                  ) : inv.status !== "cancelled" && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button size="sm" data-testid={`invoice-send-${inv.id}`} onClick={() => { setSendResend(false); setSendInv(inv); }} className="bg-navy hover:bg-navy-light">
                        <Send className="w-3.5 h-3.5 mr-1.5" />Send / Sync…
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`invoice-pdf-${inv.id}`} onClick={() => downloadInvoicePdf(inv)}>
                        <Download className="w-3.5 h-3.5 mr-1.5" />Download Invoice PDF
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`invoice-edit-${inv.id}`} onClick={() => openEditInvoice(inv)}>
                        <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit Invoice
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`invoice-reopen-${inv.id}`} onClick={() => setReopenInv(inv)}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1.5" />Reopen to Edit
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`invoice-audit-${inv.id}`} onClick={() => setAuditInv(auditInv === inv.id ? null : inv.id)}>
                        <History className="w-3.5 h-3.5 mr-1.5" />Audit History
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`invoice-cancel-${inv.id}`} onClick={() => cancelInvoice(inv)} className="text-red-600 hover:text-red-700">
                        <XCircle className="w-3.5 h-3.5 mr-1.5" />Cancel Draft Invoice
                      </Button>
                    </div>
                  )}
                  {auditInv === inv.id && (
                    <div data-testid={`invoice-audit-list-${inv.id}`} className="mt-3 border-t border-slate-100 pt-3">
                      <p className="text-xs uppercase tracking-widest font-semibold text-slate-400 mb-2">Audit History</p>
                      {(inv.audit_log || []).length === 0 ? <p className="text-xs text-slate-400">No changes recorded yet.</p> : (
                        <div className="space-y-1.5">
                          {[...(inv.audit_log || [])].reverse().map((a, i) => (
                            <div key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2">
                              <p className="font-medium text-navy">{a.action}</p>
                              {(a.to || a.cc || a.xero_invoice_id) && (
                                <p className="text-slate-500">
                                  {a.to && <span>To: {a.to}</span>}
                                  {a.cc && a.cc !== "—" && <span> · CC: {a.cc}</span>}
                                  {a.xero_invoice_id && <span> · Xero: {a.xero_invoice_id}</span>}
                                </p>
                              )}
                              <p className="text-slate-400">{a.admin} · {formatDate(a.at)} {a.old !== undefined && a.old !== "—" && <span>· {a.old} → {a.new}</span>}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-3 border-t border-slate-100 pt-3 space-y-1">
                    {inv.line_items?.map((li, i) => (
                      <div key={i} className="flex items-center justify-between text-xs text-slate-600">
                        <button onClick={() => navigate(`/jobs/${li.job_id}`)} className="text-left hover:text-navy">{formatDate(li.date)} · {li.description}{li.qty && li.unit_rate ? ` (${li.qty} × ${money(li.unit_rate)})` : ""}</button>
                        <span className="font-medium">{money(li.amount)}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-xs pt-1.5 border-t border-slate-100 mt-1.5">
                      <span className="text-slate-400">Subtotal / GST / Total</span>
                      <span className="text-navy font-semibold">{money(inv.total_amount)} + {money(inv.gst ?? 0)} = {money(inv.total_inc_gst ?? inv.total_amount)}</span>
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-2">{inv.docket_pdfs?.length || 0} docket PDF(s) attached to this invoice.</p>
                </div>
              );
            })}
          </div>
        )}

        {cancelledInvoices.length > 0 && (
          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="archived-invoices">
            <button type="button" data-testid="toggle-archived" onClick={() => setShowArchived((s) => !s)}
              className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy">
              <Archive className="w-4 h-4" />
              Cancelled / Archived Invoices ({cancelledInvoices.length})
              {showArchived ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showArchived && (
              <div className="mt-3 space-y-2">
                {cancelledInvoices.map((inv) => (
                  <div key={inv.id} data-testid={`archived-invoice-${inv.id}`} className="border border-slate-100 rounded-xl p-3 bg-slate-50/60">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-600 flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">{inv.invoice_number || "—"}</span>
                          <span>{inv.customer_name || inv.billing_company || "—"}</span>
                          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700">Cancelled</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">{formatDate(inv.week_start)} → {formatDate(inv.week_end)} · {money(inv.total_inc_gst ?? inv.total_amount)} inc GST</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">Dockets returned to Ready to Bill — edit the job(s) and raise a new invoice. This number is retired.</p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" data-testid={`archived-pdf-${inv.id}`} onClick={() => downloadInvoicePdf(inv)}>
                          <Download className="w-3.5 h-3.5 mr-1.5" />PDF
                        </Button>
                        <Button size="sm" variant="outline" data-testid={`archived-audit-${inv.id}`} onClick={() => setAuditInv(auditInv === inv.id ? null : inv.id)}>
                          <History className="w-3.5 h-3.5 mr-1.5" />History
                        </Button>
                      </div>
                    </div>
                    {auditInv === inv.id && (
                      <div className="mt-2 border-t border-slate-200 pt-2">
                        {(inv.audit_log || []).length === 0 ? <p className="text-xs text-slate-400">No changes recorded.</p> : (
                          <div className="space-y-1.5">
                            {[...(inv.audit_log || [])].reverse().map((a, i) => (
                              <div key={i} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-2">
                                <p className="font-medium text-slate-700">{a.action}</p>
                                <p className="text-slate-400">{a.admin} · {formatDate(a.at)}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <InvoicePreview open={previewOpen} onOpenChange={setPreviewOpen} preview={preview}
        invoiceNumber={null} onCreate={createInvoice} creating={creating}
        ctaLabel="Send Invoice & Sync" creatingLabel="Preparing…"
        onDownloadPdf={async () => {
          try {
            const res = await api.post("/invoices/preview-pdf", { project_id: projectId, week_start: week, docket_ids: chosenIds, invoice_notes: invoiceNotes }, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = "invoice-preview.pdf";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          } catch (e) { toast.error("Could not generate invoice PDF"); }
        }} />

      <SendInvoiceDialog inv={sendInv} open={!!sendInv} resendMode={sendResend}
        onOpenChange={(o) => !o && setSendInv(null)}
        onDone={() => { loadInvoices(); loadReady(); }} />

      <AdminEditJob open={!!jobEdit} setOpen={(o) => { if (!o) setJobEdit(null); }} job={jobEdit}
        onSaved={() => { setJobEdit(null); setJobPreviewRefresh((n) => n + 1); loadDockets(); }} />

      <EditDayDialog open={!!editDay} setOpen={(o) => { if (!o) setEditDay(null); }}
        jobId={editDay?.jobId} index={editDay?.index}
        onSaved={() => { setEditDay(null); setJobPreviewRefresh((n) => n + 1); loadDockets(); }} />

      {/* Edit invoice billing dialog */}
      <Dialog open={!!editInv} onOpenChange={(o) => !o && setEditInv(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Edit Invoice {editInv?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Invoice Date</Label><Input data-testid="edit-invoice-date" type="date" value={editForm.invoice_date} onChange={(e) => setEf("invoice_date", e.target.value)} className="mt-1" /></div>
              <div><Label>Payment Terms</Label>
                <Select value={editForm.payment_terms} onValueChange={(v) => setEf("payment_terms", v)}>
                  <SelectTrigger data-testid="edit-payment-terms" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_TERMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-slate-400">Due date recalculates from invoice date + terms on save.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Billing Company</Label><Input data-testid="edit-billing-company" value={editForm.billing_company} onChange={(e) => setEf("billing_company", e.target.value)} className="mt-1" /></div>
              <div><Label>Billing Contact</Label><Input data-testid="edit-billing-contact" value={editForm.billing_contact} onChange={(e) => setEf("billing_contact", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>Billing Address</Label><Input data-testid="edit-billing-address" value={editForm.billing_address} onChange={(e) => setEf("billing_address", e.target.value)} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Billing Email</Label><Input data-testid="edit-billing-email" value={editForm.billing_email} onChange={(e) => setEf("billing_email", e.target.value)} className="mt-1" /></div>
              <div><Label>Accounts Email</Label><Input data-testid="edit-accounts-email" value={editForm.accounts_email} onChange={(e) => setEf("accounts_email", e.target.value)} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>PO Number</Label><Input data-testid="edit-po-number" value={editForm.po_number} onChange={(e) => setEf("po_number", e.target.value)} className="mt-1" /></div>
              <div><Label>ABN</Label><Input data-testid="edit-abn" value={editForm.abn} onChange={(e) => setEf("abn", e.target.value)} className="mt-1" /></div>
            </div>
            <div><Label>Internal Invoice Notes</Label><textarea data-testid="edit-invoice-notes" rows={2} value={editForm.invoice_notes} onChange={(e) => setEf("invoice_notes", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditInv(null)}>Cancel</Button>
            <Button data-testid="edit-invoice-save" onClick={saveInvoiceEdit} className="bg-navy hover:bg-navy-light">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Reopen invoice dialog — choose destination queue */}
      <Dialog open={!!reopenInv} onOpenChange={(o) => !o && setReopenInv(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Reopen {reopenInv?.invoice_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This retires the invoice number and returns its {reopenInv?.docket_ids?.length || 0} job(s) so you can edit dockets, charge codes, rates or billing, then regenerate the invoice. Choose where the jobs should go:</p>
          <div className="grid grid-cols-1 gap-2 pt-1">
            <Button data-testid="reopen-to-waiting" variant="outline" className="justify-start h-auto py-3 text-left" onClick={() => reopenInvoice("waiting_to_invoice")}>
              <div><p className="font-semibold text-navy">Waiting to Invoice</p><p className="text-xs text-slate-500 font-normal">Re-review each job before it re-enters Weekly Billing.</p></div>
            </Button>
            <Button data-testid="reopen-to-weekly" variant="outline" className="justify-start h-auto py-3 text-left" onClick={() => reopenInvoice("ready_for_weekly_invoice")}>
              <div><p className="font-semibold text-navy">Ready to Bill</p><p className="text-xs text-slate-500 font-normal">Return straight to the Ready-for-Billing list.</p></div>
            </Button>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setReopenInv(null)}>Cancel</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
